import { configureStore, createSlice } from "@reduxjs/toolkit";
import { combineReducers } from "redux";
import storage from "redux-persist/lib/storage";
import { persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from "redux-persist";
import { PersistState } from "redux-persist/lib/types";
import axios from "axios";
import API_URL from "../config";
import React from "react";

export interface FormProps {
  formId: string;
  programId: string;
  partnerImage?: string;
  partnerName: string;
  partnerLink: string;
  completeText?: string;
  completeTitle: string;
  customUrl?: string;
  isEnd?: boolean;
  programTitle: string;
  sections: SectionProps[];
}

export interface FormEtcProps {
  partnerImage: string;
  partnerName: string;
  partnerLink: string;
  completeText?: string;
  completeTitle: string;
}

export interface PresignedUrlProps {
  preSignedUrl: string;
  savedFileUrl: string;
}

export interface SectionProps {
  formSectionId: string;
  sectionTitle: string;
  isTitle: boolean;
  cards: CardProps[];
  order?: number;
  teamNumber?: number;
}

export interface AnswerDataProps {
  formAnswerId: string;
  formId: string;
  answerSnippets: AnswerSnippetProps[];
  createdAt: string;
}

export interface AnswerSnippetProps {
  formCardId: string;
  answer: string;
  createdAt: string;
  teamIndex: number;
  url?: string;
}

export interface CardProps {
  id: string;
  cardTitle: string;
  inputType: string;
  subText?: string;
  placeholder: string;
  contents?: string | ItemTypeProps[];
  isFocused: boolean;
  isRequired: boolean;
  lengthMin?: number;
  order?: number;
}

export interface ItemTypeProps {
  id: string;
  text?: string;
  isEtc?: boolean;
  order?: number;
}

interface PayloadProps {
  [key: string]: string;
}

interface ActionProps {
  type: string;
  payload: PayloadProps;
}

export interface StateProps {
  cards: CardProps[];
  formEtcs: FormEtcProps;
  required: [];
  lengthMin: [];
  _persist: PersistState;
}

export enum InputTypes {
  TITLE = "TITLE",
  TEXT = "TEXT",
  TEXTAREA = "TEXTAREA",
  RADIO = "RADIO",
  CHECKBOX = "CHECKBOX",
  SELECT = "SELECT",
  FILE = "FILE",
  CONTACT = "CONTACT",
  NAME = "NAME",
  SECTION = "SECTION",
  ADDABLE_SECTION = "ADDABLE_SECTION",
}

const initialCards = {
  id: "TitleCard",
  cardTitle: "제목 없는 설문지",
  inputType: InputTypes.TITLE,
  subText: "",
  placeholder: "",
  contents: "",
  isFocused: false,
  isRequired: false,
};

const createNewCard = (cardId: string, cardTitle = "") => ({
  id: cardId,
  cardTitle,
  inputType: InputTypes.RADIO,
  subText: "",
  placeholder: "",
  contents: [
    {
      id: String(Number(cardId) + 1),
      text: "옵션 1",
    },
  ],
  isFocused: true,
  isRequired: false,
});

const sortEtcItem = (currentContents: ItemTypeProps[]) => {
  const etcIndex = currentContents.findIndex((content) => content.isEtc);
  if (etcIndex !== -1) {
    const etcItem = { ...currentContents[etcIndex] };
    currentContents.splice(etcIndex, 1);
    currentContents.push(etcItem);
  }
  return currentContents;
};

const deleteEtcItem = (currentContents: ItemTypeProps[]) => {
  const etcIndex = currentContents.findIndex((content) => content.isEtc);
  if (etcIndex !== -1) {
    currentContents.splice(etcIndex, 1);
  }
  return currentContents;
};

const requiredSlice = createSlice({
  name: "Required",
  initialState: [] as string[], // 상태를 문자열 배열로 설정
  reducers: {
    setRequiredCardId: (state, action: { payload: { cardId: string } }) => {
      if (!state.includes(action.payload.cardId)) {
        state.push(action.payload.cardId);
      }
    },
    removeRequiredCardId: (state, action: { payload: { cardId: string } }) => {
      const newState = state.filter((id) => id !== action.payload.cardId);

      return newState;
    },
  },
});

const lengthMinSlice = createSlice({
  name: "LengthMin",
  initialState: [] as string[], // 상태를 문자열 배열로 설정
  reducers: {
    setLengthMinCardId: (state, action: { payload: { cardId: string } }) => {
      if (!state.includes(action.payload.cardId)) {
        state.push(action.payload.cardId);
      }
    },
    removeLengthMinCardId: (state, action: { payload: { cardId: string } }) => {
      const newState = state.filter((cardId) => cardId !== action.payload.cardId);

      return newState;
    },
  },
});

const cardSlice = createSlice({
  name: "Reducer",
  initialState: [initialCards] as CardProps[],
  reducers: {
    addCard: (state: CardProps[], action: ActionProps) => {
      const copiedState = state.map((card) => ({ ...card, isFocused: false }));

      console.log(action.payload.cardId);
      if (Number(action.payload.focusedCardIndex) > 0) {
        copiedState.splice(
          Number(action.payload.focusedCardIndex) + 1,
          0,
          createNewCard(action.payload.cardId, action.payload.cardTitle),
        );
      } else {
        copiedState.push(createNewCard(action.payload.cardId, action.payload.cardTitle));
      }
      return copiedState;
    },

    copyCard: (state: CardProps[], action: ActionProps) => {
      const copiedState = state.map((card) => ({ ...card, isFocused: false }));
      const targetCard = copiedState.find((card) => card.id === action.payload.cardId) as CardProps;
      const targetCardIndex = copiedState.findIndex((card) => card.id === action.payload.cardId);
      const copiedCard = {
        ...targetCard,
        id: action.payload.copiedCardId,
        isFocused: true,
      };
      if (typeof copiedCard.contents === "object") {
        const itemTypeCopiedCardContents = copiedCard.contents.map((content, index) => ({
          ...content,
          id: String(Number(action.payload.copiedCardId) + index),
        }));
        copiedCard.contents = itemTypeCopiedCardContents;
      }
      copiedState.splice(targetCardIndex + 1, 0, copiedCard);
      return copiedState;
    },

    removeCard: (state: CardProps[], action: ActionProps) => {
      const copiedState = state.map((card) => ({ ...card, isFocused: false }));
      const targetCardIndex = copiedState.findIndex((card) => card.id === action.payload.cardId);
      const filteredState = copiedState.filter((card) => card.id !== action.payload.cardId);
      if (targetCardIndex !== 1) {
        return filteredState.map((card, index) =>
          index === targetCardIndex - 1 ? { ...card, isFocused: true } : card,
        );
      }
      if (targetCardIndex === 1) {
        return filteredState.map((card, index) =>
          index === targetCardIndex ? { ...card, isFocused: true } : card,
        );
      }

      return filteredState;
    },

    focus: (state: CardProps[], action: ActionProps) => {
      const copiedState = state.map((card) =>
        action.payload.id === card.id
          ? { ...card, isFocused: true }
          : { ...card, isFocused: false },
      );
      return copiedState;
    },

    typeChange: (state: CardProps[], action: ActionProps) => {
      const targetCard = state.find((card) => card.id === action.payload.id) as CardProps;
      if (
        !(
          targetCard.inputType === InputTypes.RADIO ||
          targetCard.inputType === InputTypes.CHECKBOX ||
          targetCard.inputType === InputTypes.SELECT
        ) &&
        (action.payload.inputType === InputTypes.RADIO ||
          action.payload.inputType === InputTypes.CHECKBOX ||
          action.payload.inputType === InputTypes.SELECT)
      ) {
        targetCard.contents = [
          {
            id: String(Date.now()),
            text: "옵션 1",
          },
        ];
      } else if (
        (targetCard.inputType === InputTypes.RADIO ||
          targetCard.inputType === InputTypes.CHECKBOX ||
          targetCard.inputType === InputTypes.SELECT) &&
        !(
          action.payload.inputType === InputTypes.RADIO ||
          action.payload.inputType === InputTypes.CHECKBOX ||
          action.payload.inputType === InputTypes.SELECT
        )
      ) {
        targetCard.contents = "";
      }
      if (
        targetCard.inputType === InputTypes.RADIO &&
        (action.payload.inputType === InputTypes.SELECT ||
          action.payload.inputType === InputTypes.CHECKBOX)
      ) {
        deleteEtcItem(targetCard.contents as ItemTypeProps[]);
      }
      targetCard.inputType = action.payload.inputType as string;
    },

    addSelectItem: (state: CardProps[], action: ActionProps) => {
      const contents = state.find((card) => card.id === action.payload.id)
        ?.contents as ItemTypeProps[];
      contents.push({ id: action.payload.contentId, text: action.payload.text });
      sortEtcItem(contents);
    },

    removeSelectItem: (state: CardProps[], action: ActionProps) => {
      const targetCard = state.find((card) => card.id === action.payload.cardId) as CardProps;
      const contents = targetCard.contents as ItemTypeProps[];
      const filteredContents = contents.filter((item) => item.id !== action.payload.contentId);
      targetCard.contents = filteredContents;
    },

    setLengthMin: (state: CardProps[], action: ActionProps) => {
      const targetCard = state.find((card) => card.id === action.payload.cardId) as CardProps;
      targetCard.lengthMin = Number(action.payload.lengthMin);
    },

    setLengthMinUndefined: (state: CardProps[], action: ActionProps) => {
      const targetCard = state.find((card) => card.id === action.payload.cardId) as CardProps;
      targetCard.lengthMin = undefined;
    },

    setTitle: (state: CardProps[], action: ActionProps) => {
      const targetCard = state.find((card) => card.id === action.payload.cardId) as CardProps;
      targetCard.cardTitle = action.payload.text;
    },

    setPlaceholder: (state: CardProps[], action: ActionProps) => {
      const targetCard = state.find((card) => card.id === action.payload.cardId) as CardProps;
      targetCard.placeholder = action.payload.text;
    },

    setText: (state: CardProps[], action: ActionProps) => {
      const targetCard = state.find((card) => card.id === action.payload.cardId) as CardProps;

      if (targetCard.inputType === InputTypes.TITLE) {
        targetCard.contents = action.payload.text;
      }
      if (
        targetCard.inputType === InputTypes.RADIO ||
        targetCard.inputType === InputTypes.CHECKBOX ||
        targetCard.inputType === InputTypes.SELECT
      ) {
        const contents = targetCard.contents as ItemTypeProps[];
        const targetContent = contents.find(
          (content) => content.id === action.payload.contentId,
        ) as ItemTypeProps;
        targetContent.text = action.payload.text;
      }
    },

    setSubText: (state: CardProps[], action: ActionProps) => {
      const targetCard = state.find((card) => card.id === action.payload.cardId) as CardProps;
      targetCard.subText = action.payload.text;
    },

    addEtcItem: (state: CardProps[], action: ActionProps) => {
      const contents = state.find((card) => card.id === action.payload.id)
        ?.contents as ItemTypeProps[];
      contents.push({ id: action.payload.contentId, isEtc: true });
    },

    toggleIsRequired: (state: CardProps[], action: ActionProps) => {
      const targetCard = state.find((card) => card.id === action.payload.id) as CardProps;
      targetCard.isRequired = !targetCard.isRequired;
    },

    moveCard: (state: CardProps[], action: ActionProps) => {
      const copiedState = [...state];
      const movingCard = copiedState.splice(Number(action.payload.sourceIndex), 1);
      copiedState.splice(Number(action.payload.destinationIndex), 0, ...movingCard);
      return copiedState;
    },

    moveContent: (state: CardProps[], action: ActionProps) => {
      const targetCard = state.find((card) => card.id === action.payload.cardId) as CardProps;
      const contents = targetCard.contents as ItemTypeProps[];
      const tmp = contents.splice(Number(action.payload.sourceIndex), 1);
      contents.splice(Number(action.payload.destinationIndex), 0, ...tmp);
    },

    // New reducer for initializing cards
    initCards: (state: CardProps[], action: { payload: CardProps[] }) => {
      const copiedState = state.filter((card) => card.id === "-10");
      copiedState.push(...action.payload);

      return copiedState;
    },
  },
});

const formEtcsSlice = createSlice({
  name: "formEtcs",
  initialState: {} as FormEtcProps,
  reducers: {
    initFormEtcs: (state: FormEtcProps, action: { payload: FormEtcProps }) => action.payload,
    editPartnerImage: (state: FormEtcProps, action: { payload: { partnerImage: string } }) => {
      // eslint-disable-next-line no-param-reassign
      state.partnerImage = action.payload.partnerImage;
    },
    editPartnerName: (state: FormEtcProps, action: { payload: { partnerName: string } }) => {
      // eslint-disable-next-line no-param-reassign
      state.partnerName = action.payload.partnerName;
    },
    editPartnerLink: (state: FormEtcProps, action: { payload: { partnerLink: string } }) => {
      // eslint-disable-next-line no-param-reassign
      state.partnerLink = action.payload.partnerLink;
    },
    editCompleteText: (state: FormEtcProps, action: { payload: { completeText: string } }) => {
      // eslint-disable-next-line no-param-reassign
      state.completeText = action.payload.completeText;
    },
    editCompleteTitle: (state: FormEtcProps, action: { payload: { completeTitle: string } }) => {
      // eslint-disable-next-line no-param-reassign
      state.completeTitle = action.payload.completeTitle;
    },
  },
});

const reducers = combineReducers({
  cards: cardSlice.reducer,
  formEtcs: formEtcsSlice.reducer,
  required: requiredSlice.reducer,
  lengthMin: lengthMinSlice.reducer,
});

const persistConfig = {
  key: "root",
  storage,
  whitelist: ["cards", "formEtcs"],
};

const persistedReducer = persistReducer(persistConfig, reducers);

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});
export type RootState = ReturnType<typeof store.getState>;
export const {
  addCard,
  copyCard,
  removeCard,
  focus,
  typeChange,
  addSelectItem,
  removeSelectItem,
  setTitle,
  setLengthMin,
  setLengthMinUndefined,
  setText,
  setPlaceholder,
  setSubText,
  addEtcItem,
  toggleIsRequired,
  moveCard,
  moveContent,
  initCards,
} = cardSlice.actions;
export const { setRequiredCardId, removeRequiredCardId } = requiredSlice.actions;

export const { setLengthMinCardId, removeLengthMinCardId } = lengthMinSlice.actions;

export const {
  initFormEtcs,
  editPartnerImage,
  editPartnerName,
  editPartnerLink,
  editCompleteText,
  editCompleteTitle,
} = formEtcsSlice.actions;

export const uploadPresignedUrl = async (file: File) => {
  console.log("upload file");
  const response = await axios.get(
    `${API_URL}/files/form/upload/pre-signed-url?fileName=${file.name}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("accessKey")}`,
      },
    },
  );
  const data = response.data.result as PresignedUrlProps;

  await axios.put(data.preSignedUrl, file, {
    headers: {
      "Content-Type": file.type,
    },
  });

  return data.savedFileUrl;
};

export default store;
